/**
 * Chat-message bubble.
 *
 * Renders the agent's markdown via `marked` + `dompurify`, then applies
 * brand-coloured highlighting of asset names and decomposition state-
 * variable drivers on the rendered DOM nodes (so the colour wrapping
 * survives nested bold / italic / table cells).
 *
 * Replaces an earlier hand-rolled tokeniser. Same visual contract.
 */
import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

import type { ChatMessage } from './types';
import './chat-md.css';

// Brand palettes — single source of truth in lib/plotly/colors plus
// the mars-forecasts types. Inlined here as the renderer touches plain
// text spans, not chart traces.
const ASSET_COLORS: Record<string, string> = {
  Equities: '#ec665c',
  Bonds: '#729dcd',
  'Fixed Income': '#729dcd',
  Commodities: '#ff9d4a',
  Inflation: '#9467bd',
  'Inflation Swaps': '#9467bd',
};

// Mirrors `CONTRIBUTOR_COLORS` in frontend/src/lib/plotly/colors.ts.
const STATE_VAR_COLORS: Record<string, string> = {
  'Real Risk-Free Rate': '#5569C9',
  'Stock Excess Return': '#DD6E63',
  'Bond Excess Return': '#1569CC',
  'Commodity Excess Return': '#F1A35B',
  'Swap Excess Return': '#93526B',
  'Price-Dividend Ratio': '#7ACAC5',
  'Dividend Growth': '#195546',
  'Term Spread': '#8D69B8',
  'Commodity Carry': '#BB9C70',
  'Commodity Price': '#2D3038',
  'Swap Rate': '#FFCE4C',
  'Credit Spread': '#7A9DCA',
  Inflation: '#93526B',
  'Real GDP': '#897871',
  'Inflation Cycle': '#BDD6EB',
};

const ASSET_NAMES = Object.keys(ASSET_COLORS).sort((a, b) => b.length - a.length);
// Longest first so 'Inflation Cycle' wins over 'Inflation'.
const STATE_VARS = Object.keys(STATE_VAR_COLORS).sort((a, b) => b.length - a.length);

// Build one compiled regex with the combined pattern so we walk the
// DOM once per text node.
const HIGHLIGHT_PATTERN = new RegExp(
  '(' +
    [...STATE_VARS, ...ASSET_NAMES].map((n) => n.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')).join('|') +
    ')',
  'g',
);

function colourFor(text: string): string | null {
  return STATE_VAR_COLORS[text] ?? ASSET_COLORS[text] ?? null;
}

// Configure marked once. Default options give us GitHub-flavored tables,
// headings, fenced code, bullets — exactly the markdown subset our
// agent emits.
marked.setOptions({ gfm: true, breaks: false });

// Sanitiser tag/attribute allowlist: same as marked emits plus the
// `style` attribute (for our colour spans) and `colspan` (tables).
const SANITIZE_OPTS = {
  ALLOWED_TAGS: [
    'a', 'b', 'blockquote', 'br', 'code', 'em', 'h1', 'h2', 'h3', 'h4',
    'h5', 'h6', 'hr', 'i', 'li', 'ol', 'p', 'pre', 'span', 'strong',
    'table', 'tbody', 'td', 'th', 'thead', 'tr', 'ul',
  ],
  ALLOWED_ATTR: ['class', 'colspan', 'rowspan', 'style', 'href', 'title', 'target', 'rel'],
};

export default function MessageBubble({ message }: { message: ChatMessage }): JSX.Element {
  const isUser = message.role === 'user';

  const html = useMemo(() => {
    if (!message.content) return '';
    const raw = marked.parse(message.content, { async: false }) as string;
    const safe = DOMPurify.sanitize(raw, SANITIZE_OPTS);
    // User bubble keeps simple white text on dark teal — no colour
    // highlighting (poor contrast and not the user's content anyway).
    return isUser ? safe : applyColourHighlighting(safe);
  }, [message.content, isUser]);

  return (
    <div className={`my-1.5 flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={
          'max-w-[88%] rounded-lg px-3 py-2 text-[0.85rem] leading-snug ' +
          (isUser
            ? 'bg-[color:var(--accent-hover)] text-white shadow-sm'
            : 'bg-white text-gray-900 border border-gray-200 chat-md')
        }
        dangerouslySetInnerHTML={{
          __html: html || (isUser ? '' : '…'),
        }}
      />
    </div>
  );
}

/**
 * Walk the rendered HTML, find every asset / state-variable name in a
 * text node, wrap it in a coloured `<span>`. Skips text inside <code>
 * / <pre> so verbatim code stays untouched.
 */
function applyColourHighlighting(html: string): string {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  walk(tpl.content);
  return tpl.innerHTML;
}

function walk(node: Node): void {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const tag = (node as Element).tagName.toLowerCase();
    if (tag === 'code' || tag === 'pre' || tag === 'a') return;
  }
  if (node.nodeType === Node.TEXT_NODE && node.textContent) {
    const text = node.textContent;
    if (!HIGHLIGHT_PATTERN.test(text)) return;
    // Build a fragment of alternating Text + coloured Span nodes.
    HIGHLIGHT_PATTERN.lastIndex = 0;
    const frag = document.createDocumentFragment();
    let cursor = 0;
    let m: RegExpExecArray | null;
    while ((m = HIGHLIGHT_PATTERN.exec(text)) !== null) {
      if (m.index > cursor) {
        frag.appendChild(document.createTextNode(text.slice(cursor, m.index)));
      }
      const span = document.createElement('span');
      const color = colourFor(m[1]);
      if (color) span.setAttribute('style', `color:${color};font-weight:600`);
      span.textContent = m[1];
      frag.appendChild(span);
      cursor = m.index + m[1].length;
    }
    if (cursor < text.length) {
      frag.appendChild(document.createTextNode(text.slice(cursor)));
    }
    (node as ChildNode).replaceWith(frag);
    return;
  }
  // Recurse — convert NodeList to Array first so replacement doesn't
  // mutate what we're iterating.
  for (const child of Array.from(node.childNodes)) walk(child);
}
